import React, {MouseEvent, useCallback, useContext} from 'react';
import classNames from 'classnames';
import {Typography} from 'components/Typography';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {MODALS} from 'modules/modals/constants';
import {showModal} from 'modules/modals/thunks';
import {FilterSwitch} from 'plugins/modules/filter/components/FilterSwitch';
import {FilterPickupTodayButtonContext} from 'plugins/modules/filter/context/FilterPickupTodayButtonContext';
import {useExpressOrderClear} from 'plugins/modules/filter/hooks/useExpressOrderClear';
import {Winery} from 'plugins/modules/filter/components/ExpressOrder/Winery';
import {WineryWorkingHours} from 'plugins/modules/filter/components/ExpressOrder/WineryWorkingHours';
import style from './style.module.scss';
import {FilterContext} from 'plugins/modules/filter/context/new';
import {useGetAbExpressOrder} from 'modules/ab-tests/hooks';
import {useExpressOrderGtag} from 'hooks/analytics/gtag/catalog/useExpressOrderGtag';

interface IExpressOrderProps {
    onApply?: (value?: string) => void;
}

export const ExpressOrder = ({onApply}: IExpressOrderProps) => {
    const dispatch = useAppDispatch();
    const {wineShop, isExpressOrderLoading} = useContext(FilterPickupTodayButtonContext);
    const {isLoading: isFiltersLoading} = useContext(FilterContext);
    const {handleClearExpressOrder} = useExpressOrderClear();
    const {isActive: isAbExpressOrder} = useGetAbExpressOrder();

    const isWineShopExists = Boolean(wineShop);
    const {sendShowWineryPopupEvent, sendClickWineryPopupEvent} = useExpressOrderGtag();

    const handleAreaClick = useCallback(() => {
        sendClickWineryPopupEvent();
        dispatch(showModal(MODALS.EXPRESS_ORDER.name)).then(() => {
            sendShowWineryPopupEvent('plp');
        });
    }, [dispatch, sendClickWineryPopupEvent, sendShowWineryPopupEvent]);

    const handleSwitchClick = useCallback(
        async (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();

            if (!wineShop) {
                handleAreaClick();
                return;
            }

            await handleClearExpressOrder();
            onApply?.(undefined);
        },
        [wineShop, handleClearExpressOrder, onApply, handleAreaClick],
    );

    return (
        <div
            className={classNames(style.main, {
                [style.withWineShop]: isWineShopExists,
                [style.isPending]: isExpressOrderLoading,
                [style.isFiltersWaiting]: isFiltersLoading,
            })}
            onClick={handleAreaClick}
            role={'button'}
            {...(isExpressOrderLoading ? {['data-skeleton-item']: ''} : {})}
        >
            <img alt={'Express order'} className={style.image} src={'/images/express-order.webp'} />

            <div className={style.contentBlock}>
                <div>
                    <Typography className={style.content} variant={'label-14-r-fluid'}>
                        Забрать сегодня
                        <br className={style.br} />
                        <span className={style.titleSecondPart}>из винотеки</span>
                        {wineShop && isAbExpressOrder && (
                            <div className={style.wineShopArea}>
                                <Winery wineShop={wineShop} />
                            </div>
                        )}
                    </Typography>
                </div>

                <div className={style.switch}>
                    <button className={style.switchButton} onClick={handleSwitchClick} type={'button'}>
                        <FilterSwitch isChecked={isWineShopExists} />
                    </button>
                </div>
            </div>
            {isAbExpressOrder && wineShop && <WineryWorkingHours wineShop={wineShop} />}

            {wineShop && !isAbExpressOrder && (
                <div className={style.wineShopArea}>
                    <Winery wineShop={wineShop} />
                    <WineryWorkingHours wineShop={wineShop} />
                </div>
            )}
        </div>
    );
};
